// Native push notification registration via Capacitor
// On native iOS: registers with APNs (if iOS responds), caches the token,
// and POSTs /api/subscribe — the POST happens regardless of whether
// registration completes, so follow-changes still reach the backend even
// when iOS push registration is silently broken.
// On web: no-op (existing Web Push / VAPID flow handles it).

import { isNative } from './capacitor';

const BACKEND = "https://azaan-backend.onrender.com";
let _registering = false;

const _debug = {
  lastEvent: null,
  lastEventAt: null,
  permResult: null,
  registered: false,
  tokenLen: 0,
  tokenSource: null,
  registrationOutcome: null,    // 'registration-event' | 'timeout' | 'registration-error' | null — sticky, doesn't get overwritten by subscribe-posted
  registrationOutcomeAt: null,
  lastSubscribePostStatus: null,
  lastSubscribePostAt: null,
  lastSubscribeError: null,
  lastFollows: null,
  lastNotify: null,
};

export function getNativePushDebug() {
  return { ..._debug };
}

const diag = (userId, stage, detail) => {
  _debug.lastEvent = stage;
  _debug.lastEventAt = new Date().toISOString();
  if (stage === 'perm-result') {
    _debug.permResult = detail?.receive || null;
  }
  if (stage === 'registration-event' || stage === 'timeout' || stage === 'registration-error') {
    _debug.registrationOutcome = stage;
    _debug.registrationOutcomeAt = new Date().toISOString();
  }
  if (stage === 'registration-event') {
    _debug.registered = true;
    _debug.tokenLen = detail?.tokenLen || 0;
  }
  if (stage === 'subscribe-posted') {
    _debug.lastSubscribePostStatus = detail?.status ?? null;
    _debug.lastSubscribePostAt = new Date().toISOString();
    _debug.lastSubscribeError = null;
  }
  if (stage === 'subscribe-failed') {
    _debug.lastSubscribeError = detail?.msg || 'unknown';
    _debug.lastSubscribePostAt = new Date().toISOString();
  }
  try {
    fetch(`${BACKEND}/api/push-diag-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, stage, detail, ts: new Date().toISOString() }),
    }).catch(() => {});
  } catch {}
};

async function postSubscribe({ apnsToken, userId, followedMounts, notifyMounts, extraPayload }) {
  try {
    const body = { platform: 'ios-native', userId, followedMounts, notifyMounts, ...extraPayload };
    if (apnsToken) body.apnsToken = apnsToken;
    const r = await fetch(`${BACKEND}/api/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    diag(userId, 'subscribe-posted', { status: r.status, hasToken: !!apnsToken });
    return r.ok;
  } catch (err) {
    diag(userId, 'subscribe-failed', { msg: String(err).slice(0, 200) });
    return false;
  }
}

export async function registerNativePush(userId, followedMounts, notifyMounts, extraPayload) {
  if (!isNative()) return null;
  if (_registering) {
    // A previous registration is still in flight (or hung). Don't gate the
    // POST on it — fire /api/subscribe immediately with the cached token if
    // we have one. This is the key fix for the "register-called sticks
    // forever" case where iOS never responds with registration event.
    const cached = localStorage.getItem('apnsToken') || null;
    _debug.lastFollows = followedMounts;
    _debug.lastNotify = notifyMounts;
    _debug.tokenSource = cached ? 'localStorage' : 'none';
    diag(userId, 'parallel-call-bypass', { hasCached: !!cached });
    await postSubscribe({ apnsToken: cached, userId, followedMounts, notifyMounts, extraPayload });
    return cached;
  }
  _registering = true;
  _debug.lastFollows = followedMounts;
  _debug.lastNotify = notifyMounts;
  diag(userId, 'start', { cap: !!window.Capacitor, webkit: !!window.webkit?.messageHandlers?.capacitor });

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    diag(userId, 'plugin-loaded', {});

    const permResult = await PushNotifications.requestPermissions();
    diag(userId, 'perm-result', { receive: permResult.receive });
    if (permResult.receive !== 'granted') {
      // No perm — but still POST follows so backend at least has them.
      // Backend won't push to us without a token, but the data is current.
      const cached = localStorage.getItem('apnsToken') || null;
      _debug.tokenSource = cached ? 'localStorage' : 'none';
      await postSubscribe({ apnsToken: cached, userId, followedMounts, notifyMounts, extraPayload });
      return cached;
    }

    let resolveToken;
    const tokenPromise = new Promise((resolve) => { resolveToken = resolve; });
    const timeout = setTimeout(() => {
      diag(userId, 'timeout', { waited: 15000 });
      resolveToken(null);
    }, 15000);  // 15s — shorter than before so the user doesn't wait forever

    const regHandle = await PushNotifications.addListener('registration', (token) => {
      clearTimeout(timeout);
      diag(userId, 'registration-event', { tokenLen: token?.value?.length || 0 });
      resolveToken(token?.value || null);
    });

    const errHandle = await PushNotifications.addListener('registrationError', (err) => {
      clearTimeout(timeout);
      diag(userId, 'registration-error', { msg: String(err?.error || err).slice(0, 300) });
      resolveToken(null);
    });

    await PushNotifications.register();
    diag(userId, 'register-called', {});

    const freshToken = await tokenPromise;

    try { await regHandle?.remove?.(); } catch {}
    try { await errHandle?.remove?.(); } catch {}

    // Token resolution: fresh > cached > null. Cache fresh tokens to localStorage
    // so future sessions can fall back to it if iOS doesn't fire registration.
    let token = freshToken;
    if (token) {
      try { localStorage.setItem('apnsToken', token); } catch {}
      _debug.tokenSource = 'fresh';
    } else {
      token = localStorage.getItem('apnsToken') || null;
      _debug.tokenSource = token ? 'localStorage' : 'none';
    }

    // POST /api/subscribe with whatever token we have (fresh, cached, or null).
    // Backend should preserve existing apnsToken if not provided. The follow
    // data still reaches backend either way.
    await postSubscribe({ apnsToken: token, userId, followedMounts, notifyMounts, extraPayload });
    return token;
  } catch (err) {
    diag(userId, 'exception', { msg: String(err).slice(0, 300) });
    // On exception, still try to POST follows with cached token.
    const cached = localStorage.getItem('apnsToken') || null;
    _debug.tokenSource = cached ? 'localStorage' : 'none';
    await postSubscribe({ apnsToken: cached, userId, followedMounts, notifyMounts, extraPayload });
    return cached;
  } finally {
    _registering = false;
  }
}

export function setupNativePushListeners(onPlayMount, onPlayAzaan) {
  if (!isNative()) return;

  import('@capacitor/push-notifications').then(({ PushNotifications }) => {
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      const data = notification.data || {};
      if (data.azaan === 'true' || data.azaan === true) {
        if (onPlayAzaan) onPlayAzaan(data.prayer || '');
      }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      const data = notification.notification.data || {};
      if ((data.azaan === 'true' || data.azaan === true) && onPlayAzaan) {
        onPlayAzaan(data.prayer || '');
      } else if (data.mount && onPlayMount) {
        onPlayMount(data.mount);
      }
    });
  }).catch(() => {});
}
